import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Typography } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs from 'dayjs';
import { formatWeekStrings } from '../../utils/dateUtils';
import { selectCurrentVenue } from '../../redux/slices/currentUserSlice';

import {
  StyledWeekContainer,
  StyledWeekTitle,
  StyledIconBack,
  StyledIconForward,
  StyledCalendarContainer,
} from './WeekNavigator.styles';

const WeekNavigator = ({ width, noPadding, renderTitle, subCaption, noCalendar, initialDate, selectedDate, onWeekChange }) => {
  const venue = useSelector(selectCurrentVenue);
  const startOfWeekIndex = venue?.startOfWeekIndex ?? 0;

  const getAdjustedStartOfWeek = useCallback(
    (date) => {
      const currentDay = date.day();
      const daysToSubtract = (currentDay - startOfWeekIndex + 7) % 7;
      return date.subtract(daysToSubtract, 'day');
    },
    [startOfWeekIndex]
  );

  const controlledDate = selectedDate ? dayjs(selectedDate) : null;
  const [startOfWeek, setStartOfWeek] = useState(() => {
    const initialDateObj = controlledDate || (initialDate ? dayjs(initialDate) : dayjs());
    return getAdjustedStartOfWeek(initialDateObj);
  });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const calendarRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    if (selectedDate) {
      setStartOfWeek(getAdjustedStartOfWeek(dayjs(selectedDate)));
    }
  }, [selectedDate, getAdjustedStartOfWeek]);

  useEffect(() => {
    // Event listener to manage clicks outside the calendar
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target) && event.target !== titleRef.current) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const { year, month, start, end } = formatWeekStrings(startOfWeek.toDate());

  const handleTitleClick = () => {
    if (noCalendar) return;
    setIsCalendarOpen((prev) => !prev);
  };

  const handleDateChange = (direction) => {
    const newStartOfWeek = direction === 'back' ? startOfWeek.subtract(7, 'day') : startOfWeek.add(7, 'day');

    // Just update the internal state if it's not a controlled component
    if (!selectedDate) {
      setStartOfWeek(newStartOfWeek);
    }

    // Notify the week change to the parent component
    if (onWeekChange) {
      onWeekChange(newStartOfWeek.toDate());
    }
  };

  const handleCalendarChange = (newDate) => {
    // Adjust the selected date to start from the correct weekday
    const newStartOfWeek = getAdjustedStartOfWeek(dayjs(newDate));

    if (!selectedDate) {
      setStartOfWeek(newStartOfWeek);
    }

    // Notify the week change to the parent component
    if (onWeekChange) {
      onWeekChange(newStartOfWeek.toDate());
    }

    setIsCalendarOpen(false);
  };

  return (
    <StyledWeekContainer sx={noPadding ? { position: 'relative', padding: 0, width } : { position: 'relative', width }}>
      {/* Navigate to the previus week */}
      <StyledIconBack onClick={() => handleDateChange('back')}>
        <ArrowBackIosIcon />
      </StyledIconBack>

      <StyledWeekTitle ref={titleRef} onClick={handleTitleClick} variant="bodyRegular" sx={{ width: '100%' }}>
        {renderTitle ? renderTitle({ month, start, end, year }) : `${month} ${start} - ${end} • ${year}`}
        {subCaption && <Typography variant="subtitle2">{subCaption}</Typography>}
      </StyledWeekTitle>

      {/*Open calendar*/}
      {isCalendarOpen && (
        <StyledCalendarContainer ref={calendarRef}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar value={startOfWeek} onChange={handleCalendarChange} />
          </LocalizationProvider>
        </StyledCalendarContainer>
      )}

      {/* Navigate to the next Week*/}
      <StyledIconForward onClick={() => handleDateChange('forward')}>
        <ArrowForwardIosIcon />
      </StyledIconForward>
    </StyledWeekContainer>
  );
};

export default WeekNavigator;
